import * as THREE from 'three';

/**
 * Настенно-потолочный кронштейн (L-уголок) по спецификации W&W. Реального меша в
 * ассетах нет (старые FBX — грубые боксы), поэтому деталь моделируется процедурно
 * из 2D-профилей (`THREE.Shape` + `ExtrudeGeometry`) — так чисто получаются
 * скруглённый торец, отверстие под ось вала и монтажные прорези.
 *
 * Локальный кадр: X — вдоль оси вала (толщина рёбер), Y — высота, Z — глубина
 * (плечо уходит в −Z к стене; скруглённый торец у +Z к валу, центр — в начале
 * координат = ось вала). Стена/потолок — один кронштейн, ceiling — поворотом узла.
 *
 * Размеры берутся из `scale` узла: `[thickness, height, armLen]` (метры).
 */
export function buildWallBracket(
  scale: readonly [number, number, number],
  material: THREE.Material,
): THREE.Group {
  const [t, height, armLen] = scale;
  const R = height / 2; // радиус скруглённого торца (= половина высоты, Ø = высоте)
  const armBody = Math.max(armLen - R, 0.001); // прямая часть плеча (до скругления)
  const plateW = 0.04; // монтажная пластина 40 мм

  const ex = { depth: t, bevelEnabled: false } as const;

  // — Плечо: профиль в плоскости (x=глубина Z, y=высота), скруглённый торец у z=0,
  //   прямая часть уходит в −z; отверстие в центре торца.
  const arm = new THREE.Shape();
  arm.moveTo(-armBody, -R);
  arm.lineTo(0, -R);
  arm.absarc(0, 0, R, -Math.PI / 2, Math.PI / 2, false); // полукруг у торца (к валу)
  arm.lineTo(-armBody, R);
  arm.lineTo(-armBody, -R);
  // Скруглённый торец плеча — сплошной (без отверстия): со стороны вала он закрывает
  // торец/шестерню, ничего не просвечивает.
  const armGeo = new THREE.ExtrudeGeometry(arm, ex);
  // профиль-x (глубина) → мировой +Z, толщина (экструзия) → −X (ребро внутрь)
  armGeo.rotateY(-Math.PI / 2);

  // — Монтажная пластина у стены (z = −armBody): профиль (x=ширина внутрь, y=высота),
  //   экструзия по Z (тонкая); две вертикальные прорези под саморезы.
  const plate = new THREE.Shape();
  plate.moveTo(-plateW, -R);
  plate.lineTo(0, -R);
  plate.lineTo(0, R);
  plate.lineTo(-plateW, R);
  plate.lineTo(-plateW, -R);
  // Два угловых (L-образных) крепёжных паза — как в спеке (Image #16): верхний «Γ»
  // (плечи вправо и вниз), нижний — его поворот на 180° (плечи влево и вверх).
  const la = 0.014; // длина плеча паза
  const sw = 0.003; // ширина паза
  plate.holes.push(lSlot(-0.013, 0.006, la, sw, Math.PI)); // верхний-левый «Γ»
  plate.holes.push(lSlot(-0.027, -0.006, la, sw, 0)); // нижний-правый (180°)
  const plateGeo = new THREE.ExtrudeGeometry(plate, ex);
  plateGeo.translate(0, 0, -armBody - t); // к стене, вплотную за плечом

  const group = new THREE.Group();
  group.add(new THREE.Mesh(armGeo, material));
  group.add(new THREE.Mesh(plateGeo, material));
  return group;
}

/**
 * L-образный (угловой) паз: два перпендикулярных плеча длиной `arm`, толщиной `w`,
 * с общим углом; `rot` поворачивает весь паз (0 — плечи вправо и вверх). Контур —
 * 6-точечная «Г», центрируется по своему bbox в точке (cx, cy).
 */
function lSlot(cx: number, cy: number, arm: number, w: number, rot: number): THREE.Path {
  const o = -arm / 2; // центрирование bbox arm×arm
  const raw: Array<[number, number]> = [
    [0, 0],
    [arm, 0],
    [arm, w],
    [w, w],
    [w, arm],
    [0, arm],
  ];
  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  const p = new THREE.Path();
  raw.forEach(([x, y], i) => {
    const lx = x + o;
    const ly = y + o;
    const rx = cx + lx * cos - ly * sin;
    const ry = cy + lx * sin + ly * cos;
    if (i === 0) p.moveTo(rx, ry);
    else p.lineTo(rx, ry);
  });
  p.closePath();
  return p;
}
